import React from 'react';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';

const Input = React.forwardRef(({
  label,
  error,
  helper,
  required,
  disabled,
  className,
  inputClassName,
  labelClassName,
  startIcon,
  endIcon,
  type = 'text',
  ...props
}, ref) => {
  const autoId = React.useId();
  const inputId = props.id || props.name || autoId;

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={inputId}
          className={twMerge(
            clsx(
              'block text-sm font-medium text-secondary-700 mb-1.5',
              labelClassName
            )
          )}
        >
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {startIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-secondary-400">
            {startIcon}
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          type={type}
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          className={twMerge(
            clsx(
              'block w-full rounded-lg border shadow-sm',
              'px-3 py-2.5 text-sm',
              'placeholder:text-secondary-400',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              'transition-colors duration-200',
              !error && 'border-secondary-300 focus:border-primary-500 focus:ring-primary-500',
              error && 'border-danger-300 text-danger-900 placeholder:text-danger-300 focus:border-danger-500 focus:ring-danger-500',
              disabled && 'bg-secondary-50 text-secondary-500 cursor-not-allowed',
              startIcon && 'pl-10',
              endIcon && 'pr-10',
              inputClassName
            )
          )}
          {...props}
        />
        {endIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-secondary-400">
            {endIcon}
          </div>
        )}
      </div>
      {(error || helper) && (
        <p
          className={clsx(
            'mt-1.5 text-xs',
            error ? 'text-danger-600' : 'text-secondary-500'
          )}
        >
          {error || helper}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export const Textarea = React.forwardRef(({
  label,
  error,
  helper,
  required,
  disabled,
  className,
  inputClassName,
  labelClassName,
  rows = 4,
  ...props
}, ref) => {
  const autoId = React.useId();
  const inputId = props.id || props.name || autoId;

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={inputId}
          className={twMerge(
            clsx(
              'block text-sm font-medium text-secondary-700 mb-1.5',
              labelClassName
            )
          )}
        >
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        disabled={disabled}
        className={twMerge(
          clsx(
            'block w-full rounded-lg border shadow-sm',
            'px-3 py-2.5 text-sm',
            'placeholder:text-secondary-400',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'transition-colors duration-200',
            !error && 'border-secondary-300 focus:border-primary-500 focus:ring-primary-500',
            error && 'border-danger-300 text-danger-900 placeholder:text-danger-300 focus:border-danger-500 focus:ring-danger-500',
            disabled && 'bg-secondary-50 text-secondary-500 cursor-not-allowed',
            inputClassName
          )
        )}
        {...props}
      />
      {(error || helper) && (
        <p
          className={clsx(
            'mt-1.5 text-xs',
            error ? 'text-danger-600' : 'text-secondary-500'
          )}
        >
          {error || helper}
        </p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export const Select = React.forwardRef(({
  label,
  error,
  helper,
  required,
  disabled,
  className,
  inputClassName,
  labelClassName,
  children,
  placeholder,
  ...props
}, ref) => {
  const autoId = React.useId();
  const inputId = props.id || props.name || autoId;

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={inputId}
          className={twMerge(
            clsx(
              'block text-sm font-medium text-secondary-700 mb-1.5',
              labelClassName
            )
          )}
        >
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      <select
        ref={ref}
        id={inputId}
        disabled={disabled}
        className={twMerge(
          clsx(
            'block w-full rounded-lg border shadow-sm',
            'px-3 py-2.5 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'transition-colors duration-200',
            !error && 'border-secondary-300 focus:border-primary-500 focus:ring-primary-500',
            error && 'border-danger-300 text-danger-900 focus:border-danger-500 focus:ring-danger-500',
            disabled && 'bg-secondary-50 text-secondary-500 cursor-not-allowed',
            inputClassName
          )
        )}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      {(error || helper) && (
        <p
          className={clsx(
            'mt-1.5 text-xs',
            error ? 'text-danger-600' : 'text-secondary-500'
          )}
        >
          {error || helper}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export const Checkbox = React.forwardRef(({
  label,
  error,
  helper,
  disabled,
  className,
  inputClassName,
  labelClassName,
  ...props
}, ref) => {
  const autoId = React.useId();
  const inputId = props.id || props.name || autoId;

  return (
    <div className={className}>
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            ref={ref}
            type="checkbox"
            id={inputId}
            disabled={disabled}
            className={twMerge(
              clsx(
                'h-4 w-4 rounded border-secondary-300',
                'text-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-0',
                'transition-colors duration-200',
                error && 'border-danger-300',
                disabled && 'cursor-not-allowed opacity-50',
                inputClassName
              )
            )}
            {...props}
          />
        </div>
        {label && (
          <label
            htmlFor={inputId}
            className={twMerge(
              clsx(
                'ml-2 text-sm text-secondary-700',
                disabled && 'opacity-50 cursor-not-allowed',
                labelClassName
              )
            )}
          >
            {label}
          </label>
        )}
      </div>
      {(error || helper) && (
        <p
          className={clsx(
            'mt-1.5 text-xs',
            error ? 'text-danger-600' : 'text-secondary-500'
          )}
        >
          {error || helper}
        </p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

export default Input;